/**
 * TimelineView — Monday-style chronological timeline (Phase 4, 2026-05-16).
 *
 * Renderuje horyzontalną oś czasu z events ułożonymi w swimlanes per item.
 * Domyślnie pokazuje ostatnie 30 dni; user może zmienić zakres.
 *
 * Props:
 *   itemType?       — filter (np. 'investment') albo null = all
 *   itemId?         — single-item timeline
 *   daysBack?       — ile dni wstecz (default 30)
 *   onItemClick?    — callback po kliknięciu na swimlane label
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
  Calendar, ChevronLeft, ChevronRight, Filter, RefreshCw,
  Building2, Briefcase, Loader2,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  getRecentActivity, getItemActivity, listActivityEventTypes,
} from '../../lib/api';


const COLOR_DOT = {
  primary:  'bg-primary',
  emerald:  'bg-emerald-500',
  amber:    'bg-amber-500',
  red:      'bg-red-500',
  blue:     'bg-blue-500',
  cyan:     'bg-cyan-500',
  indigo:   'bg-indigo-500',
  purple:   'bg-purple-500',
  zinc:     'bg-zinc-500',
};


function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function fmtShort(d) {
  return new Date(d).toLocaleDateString('pl-PL', { day: '2-digit', month: 'short' });
}


export default function TimelineView({
  itemType = null,
  itemId = null,
  daysBack: initialDays = 30,
  onItemClick,
}) {
  const [items, setItems] = useState([]);
  const [eventMeta, setEventMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [daysBack, setDaysBack] = useState(initialDays);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');

  useEffect(() => {
    listActivityEventTypes()
      .then(d => {
        const map = {};
        (d.event_types || []).forEach(t => { map[t.key] = t; });
        setEventMeta(map);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (itemType && itemId) {
        res = await getItemActivity(itemType, itemId, { limit: 200 });
      } else {
        res = await getRecentActivity({ limit: 200 });
      }
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - daysBack);
      const filtered = (res.items || []).filter(e => {
        const d = new Date(e.created_at || 0);
        return d >= cutoff;
      });
      setItems(filtered);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Timeline load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [itemType, itemId, daysBack]);

  useEffect(() => { load(); }, [load]);

  // Filter by event_type
  const filteredItems = useMemo(() => {
    if (eventTypeFilter === 'all') return items;
    return items.filter(e => e.event_type === eventTypeFilter);
  }, [items, eventTypeFilter]);

  // Build day axis
  const days = useMemo(() => {
    const arr = [];
    const end = startOfDay(new Date());
    for (let i = daysBack - 1; i >= 0; i--) {
      const d = new Date(end);
      d.setDate(end.getDate() - i);
      arr.push(d);
    }
    return arr;
  }, [daysBack]);

  // Group items by item key (itemType/itemId) — each becomes a swimlane
  const swimlanes = useMemo(() => {
    const map = {};
    for (const e of filteredItems) {
      const key = `${e.item_type}::${e.item_id}`;
      if (!map[key]) {
        map[key] = {
          key,
          item_type: e.item_type,
          item_id: e.item_id,
          events: [],
        };
      }
      map[key].events.push(e);
    }
    // Sort lanes by most recent activity
    return Object.values(map).sort((a, b) => {
      const aLast = a.events[0]?.created_at || '';
      const bLast = b.events[0]?.created_at || '';
      return bLast.localeCompare(aLast);
    }).slice(0, 30); // max 30 swimlanes
  }, [filteredItems]);

  // Map each event to a day column index
  const dayIndex = useCallback((iso) => {
    const d = startOfDay(new Date(iso));
    return days.findIndex(x => x.getTime() === d.getTime());
  }, [days]);

  const columnWidth = 32; // px per day

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4 space-y-2">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-10 w-full" />)}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="timeline-toolbar">
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <Select value={String(daysBack)} onValueChange={(v) => setDaysBack(Number(v))}>
            <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 dni</SelectItem>
              <SelectItem value="14">14 dni</SelectItem>
              <SelectItem value="30">30 dni</SelectItem>
              <SelectItem value="60">60 dni</SelectItem>
              <SelectItem value="90">90 dni</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
            <SelectTrigger className="h-8 w-[180px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie zdarzenia</SelectItem>
              {Object.entries(eventMeta).map(([k, m]) => (
                <SelectItem key={k} value={k}>
                  {m.icon} {m.label_pl}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="h-8 gap-1">
          <RefreshCw className="w-3.5 h-3.5" />
          Odśwież
        </Button>
        <div className="ml-auto text-xs text-muted-foreground">
          {swimlanes.length} elementów · {filteredItems.length} zdarzeń
        </div>
      </div>

      {/* Timeline */}
      {swimlanes.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            Brak aktywności w wybranym zakresie czasowym.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-3 overflow-x-auto">
            <div style={{ minWidth: days.length * columnWidth + 250 }}>
              {/* Header (day labels) */}
              <div className="flex sticky top-0 bg-card z-10 border-b border-border">
                <div className="w-[230px] shrink-0 px-2 py-1 text-[10px] font-semibold text-muted-foreground">
                  Item
                </div>
                {days.map((d, i) => (
                  <div
                    key={i}
                    style={{ width: columnWidth }}
                    className="shrink-0 px-1 py-1 text-[9px] text-center text-muted-foreground border-l border-border/40"
                  >
                    {fmtShort(d)}
                  </div>
                ))}
              </div>

              {/* Swimlanes */}
              <div className="divide-y divide-border">
                {swimlanes.map(lane => (
                  <div key={lane.key} className="flex items-center hover:bg-muted/30">
                    <button
                      type="button"
                      onClick={() => onItemClick && onItemClick(lane)}
                      className="w-[230px] shrink-0 px-2 py-2 text-left text-xs truncate hover:text-primary"
                      title={`${lane.item_type}: ${lane.item_id}`}
                    >
                      <div className="flex items-center gap-1">
                        {lane.item_type === 'investment'
                          ? <Building2 className="w-3 h-3 shrink-0" />
                          : <Briefcase className="w-3 h-3 shrink-0" />}
                        <span className="truncate">{lane.item_id}</span>
                      </div>
                      <div className="text-[9px] text-muted-foreground">
                        {lane.item_type} · {lane.events.length} zdarzeń
                      </div>
                    </button>
                    {/* Day grid with dots */}
                    <div className="flex relative">
                      {days.map((d, i) => (
                        <div
                          key={i}
                          style={{ width: columnWidth, height: 32 }}
                          className="shrink-0 border-l border-border/30 relative"
                        />
                      ))}
                      {/* Dots */}
                      {lane.events.map((evt) => {
                        const idx = dayIndex(evt.created_at);
                        if (idx < 0) return null;
                        const meta = eventMeta[evt.event_type] || {};
                        const color = COLOR_DOT[meta.color] || COLOR_DOT.zinc;
                        return (
                          <div
                            key={evt.id}
                            style={{
                              left: idx * columnWidth + columnWidth / 2 - 5,
                              top: 11,
                              position: 'absolute',
                            }}
                            className={`w-2.5 h-2.5 rounded-full ${color} ring-2 ring-card hover:scale-150 transition-transform cursor-help`}
                            title={`${meta.icon || ''} ${meta.label_pl || evt.event_type} · ${new Date(evt.created_at).toLocaleString('pl-PL')}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
